import { Box } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useScrollFade } from '@/shared/components/ui/scroll-fade/use-scroll-fade/use-scroll-fade';
import {
  Breadcrumb,
  Separator,
  StyledBreadcrumbs,
} from '@/shared/features/header/components/breadcrumbs/breadcrumbs.styled';
import { TBreadcrumb } from '@/shared/features/header/components/breadcrumbs/breadcrumbs.types';
import { PageTitle } from '@/shared/features/header/header.styled';
import { useBreadcrumbs } from '@/shared/features/header/hooks/use-breadcrumbs';

export const Breadcrumbs: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const breadcrumbs = useBreadcrumbs();
  const { ref, fadeStart, fadeEnd } = useScrollFade<HTMLElement>({
    axis: 'horizontal',
  });

  const isBreadcrumbObject = (
    item: TBreadcrumb | React.ReactNode,
  ): item is TBreadcrumb => {
    return (
      item !== null &&
      typeof item === 'object' &&
      !React.isValidElement(item) &&
      'label' in item
    );
  };

  const getPageTitle = (breadcrumb: TBreadcrumb) => {
    if (breadcrumb.localized) {
      return t(`pages:${breadcrumb.label}`);
    }

    return breadcrumb.label;
  };

  const getLabel = (breadcrumb: TBreadcrumb) => {
    let label = breadcrumb.label;

    if (breadcrumb.localized) {
      label = t(`pages:${breadcrumb.label}`);
    }

    return label;
  };

  const showSingleBreadcrumbAsTitle =
    breadcrumbs.length === 1 && isBreadcrumbObject(breadcrumbs[0]);

  // The page you are on is the last crumb, so a trail too long to fit scrolls
  // away from its root rather than hiding where you actually are.
  const trail = breadcrumbs
    .map((breadcrumb) =>
      isBreadcrumbObject(breadcrumb) ? breadcrumb.label : '',
    )
    .join('/');

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollLeft = ref.current.scrollWidth;
    }
  }, [trail]);

  return (
    <StyledBreadcrumbs
      ref={ref}
      aria-label="breadcrumb"
      separator={<Separator>/</Separator>}
      $axis="horizontal"
      $fadeStart={fadeStart}
      $fadeEnd={fadeEnd}
    >
      {showSingleBreadcrumbAsTitle ? (
        <PageTitle variant="h1">
          {getPageTitle(breadcrumbs[0] as TBreadcrumb)}
        </PageTitle>
      ) : (
        breadcrumbs.map((breadcrumb, index) => {
          if (React.isValidElement(breadcrumb)) {
            return <Box key={`react-element-${index}`}>{breadcrumb}</Box>;
          }

          if (isBreadcrumbObject(breadcrumb)) {
            const isLastCrumb =
              index === breadcrumbs.length - 1 && breadcrumbs.length > 1;

            return (
              <Breadcrumb
                color={isLastCrumb ? 'textSecondary' : 'textPrimary'}
                variant={isLastCrumb ? 'p7' : 'p6'}
                key={breadcrumb.link || `crumb-${index}`}
                onClick={() => breadcrumb.link && navigate(breadcrumb.link)}
              >
                {getLabel(breadcrumb)}
              </Breadcrumb>
            );
          }

          return null;
        })
      )}
    </StyledBreadcrumbs>
  );
};
